"use client";

/**
 * Everything SmartButton needs to run a multi-step CTA, in the same `{ open, modal }` shape as
 * `useHotelBookingCta`: resolving the steps, loading the guest's steps overview, deciding where a
 * click starts, the sequence state and analytics, warming the next step, and rendering the dialog.
 * SmartButton only decides when to call `start`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCtaStepsOverviewAction } from "../_logic/actions/cta-steps-overview-actions";
import {
  EMPTY_CTA_STEP_CONTACT,
  resolveCombinedConfig,
  resolveCtaSteps,
  type CtaStepContact,
  type CtaStepsOverview,
} from "../_logic/cta-steps";
import { isOverviewStepDone } from "../_logic/cta-steps-overview-display";
import { prefetchForm } from "../_logic/form-prefetch";
import { prefetchHotelBookingModal } from "../_logic/hotel-booking-modal-prefetch";
import { CtaStepSequenceDialog } from "../_components/public/CtaStepSequenceDialog";
import { useCtaStepSequence } from "./useCtaStepSequence";

interface UseLinkedStepsCtaParams {
  /** The button's own link (step 1). */
  href: string;
  /** The button's steps after `href` (see `CtaButton.linkedSteps`). */
  linkedSteps: string[] | undefined;
  eventId: number;
  landingPageId: number | null;
  primaryColor: string;
  /** The button's label, used as the dialog's title on the overview. */
  title: string;
  /** The CTA button's id, for analytics (card grid buttons have none). */
  buttonId?: string;
}

interface UseLinkedStepsCtaReturn {
  /** Whether this button runs a sequence (2+ steps); false keeps the single-modal path. */
  isSequence: boolean;
  /** Opens the dialog: the overview for a returning signed-in guest, otherwise step 1. */
  start: () => void;
  /** The dialog, or null. Render it next to the trigger. */
  modals: JSX.Element | null;
}

/** The contact saved on the guest's form steps (first value wins), to pre-fill later steps. */
function contactFromOverview(overview: CtaStepsOverview): CtaStepContact {
  return overview.steps.reduce<CtaStepContact>(
    (carried, step) =>
      step.kind === "form" && step.contact
        ? {
            email: carried.email ?? step.contact.email,
            firstName: carried.firstName ?? step.contact.firstName,
            lastName: carried.lastName ?? step.contact.lastName,
          }
        : carried,
    EMPTY_CTA_STEP_CONTACT
  );
}

/**
 * Wires a CTA button's linked steps.
 * @param params - See {@link UseLinkedStepsCtaParams}
 * @returns See {@link UseLinkedStepsCtaReturn}
 * @throws Never
 */
export function useLinkedStepsCta({
  href,
  linkedSteps,
  eventId,
  landingPageId,
  primaryColor,
  title,
  buttonId,
}: UseLinkedStepsCtaParams): UseLinkedStepsCtaReturn {
  // Hooks
  const steps = useMemo(() => resolveCtaSteps(href, linkedSteps, eventId), [href, linkedSteps, eventId]);
  // Hardcoded per-event opt-in: a form then its hotel booking that reads as one form.
  const combined = useMemo(() => resolveCombinedConfig(steps, eventId), [steps, eventId]);

  // States
  const [overviewData, setOverviewData] = useState<CtaStepsOverview | null>(null);
  // Bumped on every load and close, so a load that finishes after the guest closed the dialog
  // doesn't reopen it.
  const loadIdRef = useRef(0);

  const runner = useCtaStepSequence({
    steps,
    landingPageId,
    eventId,
    buttonId,
    // One combined form ends on its own confirmation, never the steps overview.
    finishOnOverview: !combined && (overviewData?.signedIn ?? false),
  });
  const { state, showLoading, showError, startRun, openOverview, close: closeRunner } = runner;

  // Derived values
  // A multi-step CTA needs at least two steps; one step keeps the caller's single-modal path.
  const isSequence = steps.length > 1;
  const nextStep = state?.view === "step" && state.origin === "run" ? steps[state.index + 1] : undefined;

  // Handlers
  const fetchOverview = useCallback(
    () => getCtaStepsOverviewAction({ eventId, url: href, linkedSteps }),
    [eventId, href, linkedSteps]
  );

  const start = useCallback(async () => {
    const loadId = ++loadIdRef.current;
    showLoading();
    try {
      const data = await fetchOverview();
      if (loadId !== loadIdRef.current) return;
      setOverviewData(data);
      // A signed-in guest who has done anything lands on the overview; everyone else runs the
      // steps from the start, as before.
      // A combined form always opens as the form (its own recap covers a returning guest).
      if (!combined && data.signedIn && data.steps.some(isOverviewStepDone)) openOverview(contactFromOverview(data));
      else startRun();
    } catch (error) {
      if (loadId !== loadIdRef.current) return;
      console.error("useLinkedStepsCta: failed to load the steps overview", { eventId, href }, error);
      showError();
    }
  }, [fetchOverview, showLoading, openOverview, startRun, showError, eventId, href, combined]);

  const refresh = useCallback(async () => {
    try {
      setOverviewData(await fetchOverview());
    } catch (error) {
      console.error("useLinkedStepsCta: failed to refresh the steps overview", { eventId, href }, error);
    }
  }, [fetchOverview, eventId, href]);

  const startFromClick = useCallback(() => void start(), [start]);

  const close = useCallback(() => {
    loadIdRef.current += 1;
    closeRunner();
  }, [closeRunner]);

  // Effects
  // In a run, the next step opens without another click, so warm just that one while the current
  // step is open (a visitor who stops early never pays for the rest).
  useEffect(() => {
    if (!nextStep) return;
    if (nextStep.kind === "hotel-booking") prefetchHotelBookingModal();
    else prefetchForm(nextStep.formId, landingPageId);
  }, [nextStep, landingPageId]);

  // Render
  const overviewSource = useMemo(
    () => ({ data: overviewData, refresh, retry: startFromClick }),
    [overviewData, refresh, startFromClick]
  );
  const dialogRunner = useMemo(() => ({ ...runner, close }), [runner, close]);
  const display = useMemo(() => ({ title, eventId, primaryColor }), [title, eventId, primaryColor]);
  const modals = isSequence ? (
    <CtaStepSequenceDialog
      steps={steps}
      runner={dialogRunner}
      overview={overviewSource}
      display={display}
      combined={combined}
    />
  ) : null;

  return { isSequence, start: startFromClick, modals };
}
